"use client"

import { useState, useEffect, useMemo } from "react"
import { useRouter } from "next/navigation"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Eye, EyeOff, Loader2, AlertCircle, Mail,
  ArrowRight, Shield, CheckCircle, RefreshCw,
} from "lucide-react"

type LoginStep = 'email' | 'password' | 'otp_verify' | 'set_password'

interface UserInfo {
  email: string
  fullName: string
  status: 'pending' | 'active'
  hasPassword: boolean
}

const BACKGROUND_IMAGES = [
  '/images/bedroom.webp',
  '/images/complete-house.webp',
  '/images/Kitchen.webp',
  '/images/living-room.webp',
  '/images/progressing-house.webp',
]

// ── Shared input style ───────────────────────────────────────────────────────
const inputCls = `
  h-12 rounded-xl text-sm border transition-all duration-200
  bg-white/70 backdrop-blur-sm
  border-[rgba(30,42,94,0.18)] 
  focus:border-[#c9a84c] focus:ring-2 focus:ring-[rgba(201,168,76,0.20)]
  placeholder:text-[#8c96c6] text-[#1e2a5e]
`.trim()

// ── Button style helper ───────────────────────────────────────────────────────
const primaryBtn = {
  background: 'linear-gradient(135deg, #3D5376 0%, #6B7FA6 100%)',
  boxShadow: '0 4px 16px rgba(61,83,118,0.25)',
}
function getCsrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)amora_csrf_token=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : ''
}
export default function LoginPage() {
  const router = useRouter()
  const [isPasswordReset, setIsPasswordReset] = useState(false)
  const [step, setStep] = useState<LoginStep>('email')
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [otp, setOTP] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [otpSent, setOTPSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [particles, setParticles] = useState<Array<{
    left: string
    animationDelay: string
    animationDuration: string
  }>>([])

  useEffect(() => {
    setParticles(
      [...Array(12)].map(() => ({
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${15 + Math.random() * 10}s`,
      }))
    )
  }, [])
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % BACKGROUND_IMAGES.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  // Reset toàn bộ state của các bước sau, dùng khi quay lại step 'email'
  const resetFlowState = () => {
    setIsPasswordReset(false)
    setOTPSent(false)
    setOTP("")
    setPassword("")
    setNewPassword("")
    setConfirmPassword("")
    setError(null)
  }

  // ── API helpers ────────────────────────────────────────────────────────────
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsPasswordReset(false)
    setIsLoading(true)
    try {
      const res = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json()
      if (!data.exists) {
        setError('Email này chưa được đăng ký trong hệ thống. Vui lòng liên hệ admin.')
        return
      }
      setUserInfo({ email, fullName: data.fullName, status: data.status, hasPassword: data.hasPassword })
      setStep(data.needsActivation ? 'otp_verify' : 'password')
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra. Vui lòng thử lại.')
    } finally {
      setIsLoading(false)
    }
  }

  const sendOTP = async (purpose: 'activation' | 'login' | 'password_reset') => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': getCsrfToken(),
        },
        body: JSON.stringify({ email, purpose }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setOTPSent(true)
      setCountdown(60)
      return true
    } catch (err: any) {
      setError(err.message || 'Không thể gửi mã xác thực')
      return false
    } finally {
      setIsLoading(false)
    }
  }

  // Xác định purpose hiện tại dùng chung cho gửi & verify, tránh lệch giữa 2 nơi
  const currentOtpPurpose = (): 'activation' | 'login' | 'password_reset' => {
    if (userInfo?.status === 'pending') return 'activation'
    if (isPasswordReset) return 'password_reset'
    return 'login'
  }

  const handleOTPVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      const purpose = currentOtpPurpose()

      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': getCsrfToken(),
        },
        body: JSON.stringify({ email, otp, purpose }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)

      if (userInfo?.status === 'pending' || isPasswordReset) {
        setStep('set_password')
      } else {
        router.replace(data.user.role === 'admin' ? '/admin' : data.user.role === 'customer' ? '/customer' : '/supervisor')
      }
    } catch (err: any) {
      setError(err.message || 'Mã xác thực không đúng')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (newPassword !== confirmPassword) { setError('Mật khẩu xác nhận không khớp'); return }
    if (newPassword.length < 8) { setError('Mật khẩu phải có ít nhất 8 ký tự'); return }
    setIsLoading(true)
    try {
      const endpoint = isPasswordReset ? '/api/auth/reset-password' : '/api/auth/activate-account'
      const body = isPasswordReset
        ? { email, newPassword }
        : { email, password: newPassword }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': getCsrfToken(),
        },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      router.replace(data.user.role === 'admin' ? '/admin' : data.user.role === 'customer' ? '/customer' : '/supervisor')
    } catch (err: any) {
      setError(err.message || 'Không thể đặt lại mật khẩu')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      const res = await fetch('/api/auth/login-with-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': getCsrfToken(),
        },
        body: JSON.stringify({ email, password }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      router.replace(data.user.role === 'admin' ? '/admin' : data.user.role === 'customer' ? '/customer' : '/supervisor')
    } catch (err: any) {
      setError(err.message || 'Đăng nhập thất bại')
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    setIsPasswordReset(true)
    const ok = await sendOTP('password_reset')
    if (ok) setStep('otp_verify')
    else setIsPasswordReset(false) // gửi thất bại thì không giữ flag reset lại, tránh kẹt state
  }

  // ── Step label helper ──────────────────────────────────────────────────────
  const stepLabels = ['email', 'password', 'otp_verify', 'set_password']
  const stepIndex = stepLabels.indexOf(step)

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row">

      {/* ── LEFT: Slideshow ───────────────────────────────────────────────── */}
      <div className="absolute inset-0 lg:relative lg:w-[60%] lg:h-screen overflow-hidden lg:rounded-r-[3rem]">
        {BACKGROUND_IMAGES.map((image, index) => (
          <div
            key={image}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'}`}
          >
            <div className="absolute inset-0 animate-ken-burns">
              <Image src={image} alt={`Background ${index + 1}`} fill className="object-cover" priority={index === 0} quality={90} />
            </div>
          </div>
        ))}

        {/* Gradient overlay — nhẹ hơn để hình rõ hơn trên mobile */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(15,23,60,0.45) 0%, rgba(15,23,60,0.12) 50%, rgba(15,23,60,0.40) 100%)'
        }} />

        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {particles.map((p, i) => (
          <div key={i} className="particle" style={{
            left: p.left,
            animationDelay: p.animationDelay,
            animationDuration: p.animationDuration,
          }}>
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(201,168,76,0.35)' }} />
            </div>
          ))}
        </div>

        {/* Desktop brand overlay */}
        <div className="hidden lg:flex absolute inset-0 items-center justify-center z-10 px-16">
          <div className="text-center animate-fade-in-down">
            {/* Logo */}
            <div className="inline-flex items-center justify-center gap-4 mb-6">
              <span className="text-6xl xl:text-7xl font-serif font-light tracking-widest text-white drop-shadow-2xl">
                AMORA
              </span>
              <div className="w-px h-16 opacity-60" style={{ background: 'linear-gradient(180deg, transparent, #c9a84c, transparent)' }} />
              <span className="text-6xl xl:text-7xl font-serif font-light tracking-widest text-white drop-shadow-2xl">
                HOMES
              </span>
            </div>
            <p className="text-white/90 text-lg italic font-light tracking-wide drop-shadow-lg mb-10">
              Theo dõi ngôi nhà của bạn – sự chuẩn mực trong từng giai đoạn.
            </p>

            {/* Feature bullets */}
            <div className="flex flex-col gap-3 items-start max-w-xs mx-auto">
              {[
                'Theo dõi tiến độ theo thời gian thực',
                'Hồ sơ & thiết kế tập trung một nơi',
                'Kết nối trực tiếp với giám sát viên',
              ].map((text) => (
                <div key={text} className="flex items-center gap-2.5">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(201,168,76,0.25)', border: '1px solid rgba(201,168,76,0.55)' }}
                  >
                    <CheckCircle className="w-3 h-3" style={{ color: '#c9a84c' }} />
                  </div>
                  <span className="text-sm text-white/85">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Image indicator dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {BACKGROUND_IMAGES.map((_, i) => (
            <div
              key={i}
              className="rounded-full transition-all duration-500"
              style={{
                width: i === currentImageIndex ? '20px' : '6px',
                height: '6px',
                background: i === currentImageIndex ? '#c9a84c' : 'rgba(255,255,255,0.45)',
              }}
            />
          ))}
        </div>
      </div>

      {/* ── RIGHT: Login panel ────────────────────────────────────────────── */}
      {/* 
        FIX: Trên mobile không set background → hình slideshow hiện xuyên qua
        Trên desktop (lg:) mới có background xanh nhạt
      */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-6 lg:p-10 xl:p-14 min-h-screen lg:bg-[#eef1f8]">

        {/* Subtle top accent on mobile */}
        <div
          className="lg:hidden absolute top-0 left-0 right-0 h-1"
          style={{ background: 'linear-gradient(90deg, #1e2a5e, #c9a84c, #1e2a5e)' }}
        />

        <div className="w-full max-w-sm xl:max-w-md space-y-6">

          {/* Mobile brand — navy/gold, drop shadow để đọc được trên hình */}
          <div className="lg:hidden text-center animate-fade-in-down">
            <div className="inline-flex items-center justify-center gap-2.5 mb-2">
              <span
                className="text-2xl font-serif font-light tracking-widest"
                style={{ color: '#ffffff', textShadow: '0 2px 12px rgba(15,23,60,0.7)' }}
              >
                AMORA
              </span>
              <div className="w-px h-7" style={{ background: 'linear-gradient(180deg, transparent, #c9a84c, transparent)' }} />
              <span
                className="text-2xl font-serif font-light tracking-widest"
                style={{ color: '#ffffff', textShadow: '0 2px 12px rgba(15,23,60,0.7)' }}
              >
                HOMES
              </span>
            </div>
            <p className="text-xs italic text-white/90 drop-shadow">
              Theo dõi ngôi nhà của bạn – sự chuẩn mực trong từng giai đoạn.
            </p>
          </div>

          {/* Login card */}
          <div
            className="rounded-3xl p-7 sm:p-8 animate-fade-in-up"
            style={{
              background: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(30,42,94,0.10)',
              boxShadow: '0 8px 40px rgba(30,42,94,0.14), 0 1px 0 rgba(255,255,255,0.80) inset',
            }}
          >

            {/* Progress dots */}
            {step !== 'email' && (
              <div className="flex items-center justify-center gap-1.5 mb-5">
                {['email', 'password', 'otp_verify', 'set_password'].map((s, i) => (
                  <div
                    key={s}
                    className="rounded-full transition-all duration-400"
                    style={{
                      width: step === s ? '20px' : '6px',
                      height: '6px',
                      background: i <= stepIndex ? '#c9a84c' : 'rgba(30,42,94,0.15)',
                    }}
                  />
                ))}
              </div>
            )}

            {/* Error */}
            {error && (
              <div
                className="mb-5 flex items-start gap-2.5 p-3.5 rounded-xl animate-shake text-xs leading-relaxed"
                style={{
                  background: 'rgba(220,38,38,0.07)',
                  border: '1px solid rgba(220,38,38,0.20)',
                  color: '#b91c1c',
                }}
              >
                <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* ── STEP 1: Email ───────────────────────────────────────────── */}
            {step === 'email' && (
              <>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold mb-1" style={{ color: '#1e2a5e' }}>Đăng nhập</h2>
                  <p className="text-sm" style={{ color: '#6b7db3' }}>Nhập email để tiếp tục</p>
                </div>

                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs font-semibold" style={{ color: '#1e2a5e' }}>
                      Email
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#8c96c6' }} />
                      <Input
                        id="email" type="email" placeholder="email@example.com"
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        required disabled={isLoading} autoComplete="email"
                        className={`${inputCls} pl-10`}
                      />
                    </div>
                  </div>

                  <Button
                    type="submit" disabled={isLoading}
                    className="w-full h-12 text-white rounded-xl font-semibold transition-all duration-200 hover:opacity-90 hover:shadow-lg"
                    style={primaryBtn}
                  >
                    {isLoading
                      ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Đang kiểm tra...</>
                      : <>Tiếp tục <ArrowRight className="ml-1.5 w-4 h-4" style={{ color: '#c9a84c' }} /></>}
                  </Button>
                </form>
              </>
            )}

            {/* ── STEP 2: Password ────────────────────────────────────────── */}
            {step === 'password' && (
              <>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold mb-1" style={{ color: '#1e2a5e' }}>
                    Xin chào, {userInfo?.fullName}
                  </h2>
                  <p className="text-sm" style={{ color: '#6b7db3' }}>Nhập mật khẩu để tiếp tục</p>
                </div>

                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-xs font-semibold" style={{ color: '#1e2a5e' }}>
                      Mật khẩu
                    </Label>
                    <div className="relative">
                      <Input
                        id="password" type={showPassword ? "text" : "password"}
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        required disabled={isLoading} autoComplete="current-password"
                        className={`${inputCls} pr-11`}
                      />
                      <button
                        type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors"
                        style={{ color: '#8c96c6' }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit" disabled={isLoading}
                    className="w-full h-12 text-white rounded-xl font-semibold hover:opacity-90"
                    style={primaryBtn}
                  >
                    {isLoading
                      ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Đang đăng nhập...</>
                      : 'Đăng nhập'}
                  </Button>

                  <button
                    type="button" onClick={handleForgotPassword} disabled={isLoading}
                    className="w-full text-center text-sm font-medium transition-colors hover:opacity-80"
                    style={{ color: '#c9a84c' }}
                  >
                    Quên mật khẩu? Đăng nhập bằng mã xác thực
                  </button>

                  <button
                    type="button"
                    onClick={() => { setStep('email'); resetFlowState() }}
                    className="w-full text-center text-xs transition-colors hover:opacity-80"
                    style={{ color: '#8c96c6' }}
                  >
                    ← Đổi tài khoản
                  </button>
                </form>
              </>
            )}

            {/* ── STEP 3: OTP ─────────────────────────────────────────────── */}
            {step === 'otp_verify' && (
              <>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold mb-1" style={{ color: '#1e2a5e' }}>
                    {userInfo?.status === 'pending'
                      ? 'Kích hoạt tài khoản'
                      : isPasswordReset ? 'Quên mật khẩu' : 'Nhập mã xác thực'}
                  </h2>
                  <p className="text-sm" style={{ color: '#6b7db3' }}>
                    {otpSent
                      ? <>Mã 6 số đã được gửi đến <strong style={{ color: '#1e2a5e' }}>{email}</strong></>
                      : <>Bạn sẽ nhận mã qua email <strong style={{ color: '#1e2a5e' }}>{email}</strong></>}
                  </p>
                </div>

                {!otpSent ? (
                  <div className="space-y-4">
                    <div
                      className="p-4 rounded-xl text-xs leading-relaxed space-y-1.5"
                      style={{ background: 'rgba(30,42,94,0.05)', border: '1px solid rgba(30,42,94,0.12)' }}
                    >
                      <p className="font-bold" style={{ color: '#1e2a5e' }}>ℹ️ Hướng dẫn:</p>
                      <ol className="list-decimal ml-4 space-y-1" style={{ color: '#6b7db3' }}>
                        <li>Nhấn nút "Gửi mã xác thực" bên dưới</li>
                        <li>Kiểm tra email của bạn</li>
                        <li>Nhập mã 6 số vào ô bên dưới</li>
                        <li>Tiếp tục để {userInfo?.status === 'pending' || isPasswordReset ? 'tạo mật khẩu' : 'đăng nhập'}</li>
                      </ol>
                    </div>

                    <Button
                      type="button"
                      onClick={() => sendOTP(currentOtpPurpose())}
                      disabled={isLoading}
                      className="w-full h-12 text-white rounded-xl font-semibold hover:opacity-90"
                      style={primaryBtn}
                    >
                      {isLoading
                        ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Đang gửi...</>
                        : <><Mail className="mr-2 h-4 w-4" style={{ color: '#c9a84c' }} />Gửi mã xác thực</>}
                    </Button>

                    <button
                      type="button"
                      onClick={() => { setStep('email'); resetFlowState() }}
                      className="w-full text-center text-xs hover:opacity-80" style={{ color: '#8c96c6' }}
                    >
                      ← Quay lại
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleOTPVerify} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="otp" className="text-xs font-semibold" style={{ color: '#1e2a5e' }}>
                        Mã xác thực
                      </Label>
                      <Input
                        id="otp" type="text" placeholder="000000"
                        value={otp}
                        onChange={(e) => setOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required maxLength={6} disabled={isLoading} autoComplete="one-time-code"
                        className="h-14 text-center text-3xl tracking-[0.5em] rounded-xl font-mono border"
                        style={{
                          color: '#1e2a5e',
                          borderColor: 'rgba(30,42,94,0.18)',
                          background: 'rgba(255,255,255,0.70)',
                        }}
                      />
                    </div>

                    {/* OTP progress dots */}
                    <div className="flex gap-1.5 justify-center">
                      {[...Array(6)].map((_, i) => (
                        <div
                          key={i}
                          className="w-2 h-2 rounded-full transition-all duration-200"
                          style={{ background: i < otp.length ? '#c9a84c' : 'rgba(30,42,94,0.12)' }}
                        />
                      ))}
                    </div>

                    <Button
                      type="submit" disabled={isLoading || otp.length !== 6}
                      className="w-full h-12 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50"
                      style={primaryBtn}
                    >
                      {isLoading
                        ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Đang xác thực...</>
                        : 'Xác nhận'}
                    </Button>

                    {countdown > 0 ? (
                      <p className="text-center text-xs" style={{ color: '#8c96c6' }}>
                        Gửi lại sau <span className="font-bold" style={{ color: '#c9a84c' }}>{countdown}s</span>
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={() => sendOTP(currentOtpPurpose())}
                        disabled={isLoading}
                        className="w-full text-center text-sm font-semibold flex items-center justify-center gap-1 hover:opacity-80"
                        style={{ color: '#c9a84c' }}
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Gửi lại mã xác thực
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => { setStep('email'); resetFlowState() }}
                      className="w-full text-center text-xs hover:opacity-80" style={{ color: '#8c96c6' }}
                    >
                      ← Quay lại
                    </button>
                  </form>
                )}
              </>
            )}

            {/* ── STEP 4: Set Password ─────────────────────────────────────── */}
            {step === 'set_password' && (
              <>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold mb-1" style={{ color: '#1e2a5e' }}>
                    {isPasswordReset ? 'Đặt lại mật khẩu' : 'Tạo mật khẩu'}
                  </h2>
                  <p className="text-sm" style={{ color: '#6b7db3' }}>
                    {isPasswordReset
                      ? 'Nhập mật khẩu mới cho tài khoản của bạn'
                      : 'Vui lòng tạo mật khẩu để bảo vệ tài khoản'}
                  </p>
                </div>

                <form onSubmit={handleSetPassword} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password" className="text-xs font-semibold" style={{ color: '#1e2a5e' }}>
                      Mật khẩu mới
                    </Label>
                    <div className="relative">
                      <Input
                        id="new-password" type={showPassword ? "text" : "password"}
                        placeholder="Ít nhất 8 ký tự"
                        value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                        required minLength={8} disabled={isLoading} autoComplete="new-password"
                        className={`${inputCls} pr-11`}
                      />
                      <button
                        type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2"
                        style={{ color: '#8c96c6' }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password" className="text-xs font-semibold" style={{ color: '#1e2a5e' }}>
                      Xác nhận mật khẩu
                    </Label>
                    <Input
                      id="confirm-password" type={showPassword ? "text" : "password"}
                      placeholder="Nhập lại mật khẩu"
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      required disabled={isLoading} autoComplete="new-password"
                      className={inputCls}
                    />
                    {/* Password match indicator */}
                    {confirmPassword.length > 0 && (
                      <div className="flex items-center gap-1.5 text-[11px]">
                        <div
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: newPassword === confirmPassword ? '#12b76a' : '#dc2626' }}
                        />
                        <span style={{ color: newPassword === confirmPassword ? '#059669' : '#dc2626' }}>
                          {newPassword === confirmPassword ? 'Mật khẩu khớp' : 'Mật khẩu chưa khớp'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Tips */}
                  <div
                    className="p-3 rounded-xl text-xs space-y-1"
                    style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.22)' }}
                  >
                    <p className="font-bold flex items-center gap-1.5" style={{ color: '#1e2a5e' }}>
                      <Shield className="w-3.5 h-3.5" style={{ color: '#c9a84c' }} />
                      Gợi ý mật khẩu an toàn
                    </p>
                    {['Ít nhất 8 ký tự', 'Có chữ hoa và chữ thường', 'Có ít nhất 1 số'].map((tip) => (
                      <div key={tip} className="flex items-center gap-1.5 ml-5" style={{ color: '#6b7db3' }}>
                        <div className="w-1 h-1 rounded-full" style={{ background: '#c9a84c' }} />
                        {tip}
                      </div>
                    ))}
                  </div>

                  <Button
                    type="submit" disabled={isLoading}
                    className="w-full h-12 text-white rounded-xl font-semibold hover:opacity-90"
                    style={primaryBtn}
                  >
                    {isLoading
                      ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Đang tạo...</>
                      : 'Hoàn tất'}
                  </Button>
                </form>
              </>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-xs animate-fade-in-up text-white/70 lg:text-[#8c96c6] drop-shadow lg:drop-shadow-none">
            © 2026 Amora Homes. Bảo mật và riêng tư.
          </p>
        </div>
      </div>

      {/* ── Styles ───────────────────────────────────────────────────────── */}
      <style jsx>{`
        @keyframes ken-burns {
          0%   { transform: scale(1) translate(0, 0); }
          50%  { transform: scale(1.15) translate(-3%, -2%); }
          100% { transform: scale(1) translate(0, 0); }
        }
        @keyframes fade-in-down {
          from { opacity: 0; transform: translateY(-24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-4px); }
          40%, 80% { transform: translateX(4px); }
        }
        @keyframes float {
          0%   { transform: translateY(0) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(-100vh) translateX(60px) rotate(360deg); opacity: 0; }
        }
        .animate-ken-burns    { animation: ken-burns 20s ease-in-out infinite; }
        .animate-fade-in-down { animation: fade-in-down 0.7s ease-out both; }
        .animate-fade-in-up   { animation: fade-in-up 0.7s ease-out 0.15s both; }
        .animate-shake        { animation: shake 0.45s ease-in-out; }
        .particle {
          position: absolute;
          animation: float linear infinite;
        }
      `}</style>
    </div>
  )
}